/**
 * Per-tick animation state advancers for entities.
 *
 * - `tilt` — 0 (upright) → 1 (on-side) when incapacitated/dead; decays back
 *   toward 0 during recovery. The renderer maps this to sprite rotation.
 * - `hitShake` — jitter budget after taking damage; decays to 0.
 * - `attackSkew` — italic-lean in the swing direction after firing/striking;
 *   decays to 0.
 * - `flashTimer` — brief tint-toward-white on fire/strike; decays to 0.
 *
 * The sim owns these because they're deterministic (tied to tickDt), but
 * only the renderer *reads* them.
 */

import { isIncapacitated, type BodyState } from './body';
import type { Entity } from './world';

const TILT_ATTACK_SEC = 0.35;
const TILT_RECOVER_SEC = 1.2;

export function advanceEntityAnimation(
  e: Entity,
  body: BodyState | null,
  tickDt: number,
): void {
  // Tilt: incapacitated → +, else → -.
  const wantsDown = body ? isIncapacitated(body) : false;
  if (wantsDown) {
    e.tilt = Math.min(1, e.tilt + tickDt / TILT_ATTACK_SEC);
  } else if (e.tilt > 0) {
    e.tilt = Math.max(0, e.tilt - tickDt / TILT_RECOVER_SEC);
  }
  // Recoil / skew / flash all decay to zero.
  if (e.hitShake > 0) e.hitShake = Math.max(0, e.hitShake - tickDt * 6);
  if (Math.abs(e.attackSkew) > 0.001) e.attackSkew *= 1 - Math.min(1, tickDt * 8);
  else e.attackSkew = 0;
  if (e.flashTimer > 0) e.flashTimer = Math.max(0, e.flashTimer - tickDt * 6);
}

/** Kick off an attack-swing skew in the direction of a delta vector. `flash`
 *  (default true) adds the white muzzle flash — pass false for silenced weapons. */
export function triggerAttackAnim(e: Entity, dx: number, dy: number, flash = true): void {
  // Skew magnitude peaks at 0.35 rad (~20°). Sign follows dx primarily; if
  // strictly vertical, sign follows dy to avoid a dead-zero skew.
  const sign = dx !== 0 ? Math.sign(dx) : Math.sign(dy);
  e.attackSkew = 0.4 * sign;
  if (flash) e.flashTimer = 0.18;
}

/** Kick off a "just got hit" recoil on the target. */
export function triggerHitAnim(e: Entity): void {
  e.hitShake = 1;
  e.flashTimer = 0.12;
}
